import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { studyEntriesTableReducer } from "./StudyEntriesTable.store";
import {
  mapDispatchToProps,
  mapStateToProps,
} from "./StudyEntriesTable.store.js";
import StudyEntriesTable from "../../library/StudyEntriesTable/StudyEntriesTable";
import CustomModal from "../../ui-library/CustomModal/CustomModal";
import DietDiary from "../DietDiary/DietDiary";
import {
  DietDairyTypes,
  dietDairyTypeToString,
} from "../DietDiary/DietDiary.constants";
import { ToastType } from "../../ToastManager/constants";
import { sendNotification } from "../../ToastManager/ToastManager";
import { validDateString } from "../../../modules/utils";
import ChangeRequest from "../ChangeRequest/ChangeRequest";
import { useTranslation } from "react-i18next";
import { UserStatus } from "../../library/AccountStatus/AccountStatus.constants";
import {
  DEFAULT_REASON_FOR_CHANGE,
  getExtendedDateFormat,
} from "../../../modules/constants";
import ConfirmRequest from "../../library/ConfirmRequest/ConfirmRequest";
import { getMealEntriesString } from "../../../helpers/mealsHelper";

export const SmartStudyEntriesTable = (props) => {
  const {
    dateRange: { startDate = null, endDate = null } = {},
    fetchEntriesTableData,
    fetchReportEntriesData,
    resetState,
    entries,
    totalItems,
    searchedParams,
    canCreateChangeRequestMealLog,
    canViewDetailMealLog,
    canChangeStudyStatus,
    canViewActionButton,
    canViewStudyStatus,
    canEditParticipant,
    canGenerateReport,
    canManageUsers,
    study,
    participantId,
    userData,
    onUpdate,
    loading,
    canDeleteMeal,
    roleName,
    deleteEntryMeal,
    getParticipantInfo,
    participant,
    canCreate24hRecall,
    canCreatePaperRecapture,
    fetchDailyTargetHistory,
  } = props;

  const { t } = useTranslation();

  const [selectedDietDiaryOption, setSelectedDietDiaryOption] = useState(null);
  const [selectedEntryMeal, setSelectedEntryMeal] = useState(null);
  const [dietDiaryModalTitle, setDietDiaryModalTitle] = useState("");
  const [changeRequestModalTitle, setChangeRequestModalTitle] = useState("");
  const [entryToDelete, setEntryToDelete] = useState(null);

  const [deletingMeal, setDeletingMeal] = useState(false);

  useEffect(() => {
    if (canViewActionButton && participantId && !participant) {
      getParticipantInfo(participantId).catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
    }
  }, [canViewActionButton, participantId, participant]);

  const getData = useCallback(
    (params) => {
      fetchEntriesTableData(params, startDate, endDate, participantId).catch(
        (error) => {
          sendNotification(error, ToastType.ERROR);
        }
      );
    },
    [fetchEntriesTableData, startDate, endDate, participantId]
  );

  const onCloseDietDiaryModal = () => {
    setSelectedDietDiaryOption(null);
  };

  const refetchData = () => {
    onUpdate();
    getData(searchedParams); // re-fetch dates and data
  };

  const onSubmitNewMeal = () => {
    setSelectedDietDiaryOption(null);
    refetchData();
  };

  const onSubmitChangeRequest = () => {
    setSelectedEntryMeal(null);
  };

  const editMeal = (meal) => {
    const type = JSON.parse(meal.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setChangeRequestModalTitle(
      `${t("appText.editing")} ${userData?.participantUgnxId} ${t(
        "appText.entry"
      )}`
    );

    setSelectedEntryMeal({
      mealId: meal.mealId,
      jsonData: meal.json,
      date: meal.dateTime,
      type: type,
      reason: meal.reason,
      administeredBy: meal.administeredBy,
      feedingType: meal.feedingType,
      endDate: meal.endDate,
    });
  };

  const viewMeal = (meal) => {
    const type = JSON.parse(meal.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setDietDiaryModalTitle(
      `${t("appText.view")} ${dietDairyTypeToString(type)}`
    );

    setSelectedDietDiaryOption({
      mealId: meal.mealId,
      jsonData: meal.json,
      date: meal.dateTime,
      readOnly: true,
      type: type,
      reason: meal.reason,
      reasonForChange: meal.reasonForChange,
      administeredBy: meal.administeredBy,
      feedingType: meal.feedingType,
      endDate: meal.endDate,
    });
  };

  const onDeleteEntryMeal = (meal) => {
    setDeletingMeal(true);

    const formData = {
      mealDate: meal.dateTime,
      mealJson: meal.json,
      participantId: participantId,
      recall: meal.is24hRecall,
      administeredBy: meal.administeredBy,
      feedingType: meal.feedingType,
      paperRecapture: meal.paperRecapture,
      mealEndDate: meal.endDate,
      mealID: meal.mealId,
      reasonForChange: DEFAULT_REASON_FOR_CHANGE.deleteMeal,
      toDelete: true,
    };

    if (formData.paperRecapture) {
      formData.reasonForChange = DEFAULT_REASON_FOR_CHANGE.paperRecapture;
    }

    deleteEntryMeal(formData)
      .then((success) => {
        setEntryToDelete(null);

        sendNotification(success, ToastType.SUCCESS);

        refetchData();
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      })
      .finally(() => {
        setDeletingMeal(false);
      });
  };

  useEffect(() => {
    if (totalItems === null) {
      if (validDateString(startDate) && validDateString(endDate)) {
        getData(searchedParams);
      }
    }
  }, [endDate, getData, searchedParams, startDate, totalItems]);

  useEffect(() => {
    if (validDateString(startDate) && validDateString(endDate)) {
      getData(searchedParams);
    }
  }, [endDate, getData, searchedParams, startDate]);

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  const minDate = useMemo(() => {
    return moment().subtract(1, "days").toDate();
  }, []);

  const canCreateChangeRequestEntry = useMemo(() => {
    return (
      canCreateChangeRequestMealLog &&
      userData?.accountStatus === UserStatus.ACTIVE.id
    );
  }, [canCreateChangeRequestMealLog, userData]);

  const minDateMeal = useMemo(() => {
    return moment(userData?.participantDateOfEnrollment)
      .add(1, "days")
      .toDate();
  }, [userData]);

  return (
    <>
      <CustomModal
        title={t("appText.confirmRequest.title")}
        onClose={() => setEntryToDelete(null)}
        show={!!entryToDelete}
      >
        <ConfirmRequest
          message={`${t("appText.dietDiary.removeEntries.message", {
            date: `<br/><b>${moment(entryToDelete?.dateTime).format(
              getExtendedDateFormat()
            )}</b>`,
          })}<br/><br/>${getMealEntriesString(entryToDelete)}.`}
          warningMessage={t("appText.dietDiary.removeEntries.confirm")}
          onConfirm={() => onDeleteEntryMeal(entryToDelete)}
          onClose={() => setEntryToDelete(null)}
          submitting={deletingMeal}
        />
      </CustomModal>

      <CustomModal
        show={selectedDietDiaryOption !== null}
        title={dietDiaryModalTitle ?? ""}
        size="lg"
        onClose={() => onCloseDietDiaryModal()}
      >
        <DietDiary
          type={selectedDietDiaryOption?.type}
          participantId={participantId}
          onSubmit={() => onSubmitNewMeal()}
          onClose={() => onCloseDietDiaryModal()}
          maxDate={moment().toDate()}
          minDate={minDate}
          readOnly={selectedDietDiaryOption?.readOnly}
          mealId={selectedDietDiaryOption?.mealId}
          mealDate={selectedDietDiaryOption?.date}
          mealJsonData={selectedDietDiaryOption?.jsonData}
          dietDiaryOption={selectedDietDiaryOption}
          setModalTitle={setDietDiaryModalTitle}
          existingReasonForChange={selectedDietDiaryOption?.reasonForChange}
          mealAdministeredBy={selectedDietDiaryOption?.administeredBy}
          mealFeedingType={selectedDietDiaryOption?.feedingType}
          mealEndDate={selectedDietDiaryOption?.endDate}
          participantLanguage={participant?.language}
        />
      </CustomModal>

      <CustomModal
        show={selectedEntryMeal !== null && userData !== null}
        title={changeRequestModalTitle ?? ""}
        size="lg"
        onClose={() => setSelectedEntryMeal(null)}
      >
        <ChangeRequest
          type={selectedEntryMeal?.type}
          participantId={participantId}
          participantUsername={userData?.participantUgnxId}
          onSubmit={() => onSubmitChangeRequest()}
          maxDate={moment().toDate()}
          minDate={minDateMeal}
          mealId={selectedEntryMeal?.mealId}
          mealDate={selectedEntryMeal?.date}
          mealJsonData={selectedEntryMeal?.jsonData}
          entryMeal={selectedEntryMeal}
          setModalTitle={setChangeRequestModalTitle}
          participantLanguage={userData?.participantLanguage}
          mealAdministeredBy={selectedEntryMeal?.administeredBy}
          mealFeedingType={selectedEntryMeal?.feedingType}
          mealEndDate={selectedEntryMeal?.endDate}
          refetchData={refetchData}
        />
      </CustomModal>

      <StudyEntriesTable
        {...{ study }}
        {...{ getData }}
        {...{ searchedParams }}
        {...{ entries }}
        totalitems={totalItems}
        loading={loading}
        {...{ canViewDetailMealLog }}
        canCreateChangeRequestMealLog={canCreateChangeRequestEntry}
        canChangeStudyStatus={canChangeStudyStatus}
        canViewActionButton={canViewActionButton}
        canViewStudyStatus={canViewStudyStatus}
        canEditParticipant={canEditParticipant}
        canGenerateReport={canGenerateReport}
        canManageUsers={canManageUsers}
        participant={participant}
        {...{ fetchReportEntriesData }}
        {...{ editMeal }}
        {...{ viewMeal }}
        canDeleteMeal={canDeleteMeal}
        roleName={roleName}
        deleteMeal={(meal) => setEntryToDelete(meal)}
        refetchData={refetchData}
        userData={userData}
        {...{ canCreate24hRecall }}
        {...{ canCreatePaperRecapture }}
        {...{ fetchDailyTargetHistory }}
      />
    </>
  );
};

export default dynamic(
  "studyEntriesTableReducer",
  subspaced({
    mapExtraState: (state) => ({ loginReducer: state.loginReducer }),
  }),
  attachReducer(studyEntriesTableReducer)
)(connect(mapStateToProps, mapDispatchToProps)(SmartStudyEntriesTable));
